<script>
import { mapState } from 'vuex';
import _ from 'lodash';
import numeral from 'numeral';
import CRTableIndicatorDataTypeCell from './CRTableIndicatorDataTypeCell';

export default {
  components: {
    CRTableIndicatorDataTypeCell,
  },
  props: {
    catData: {
      type: Array,
      default() {
        return [];
      },
    },
    categoryId: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapState({
      lku: state => state.lku,
      dataTypes: state => state.dataTypes,
      compareColumns: state => state.CR.compareColumns,
      reportType: state => state.CR.GSGo.reportType,
      showCI: state => state.CR.reportOptionsGo.showCI,
      showWN: state => state.CR.reportOptionsGo.showWN,
    }),
    printerFriendly() {
      return this.$store.getters.printerFriendly;
    },
    rowsMinusColumnValues() {
      return _(this.catData).uniqBy(o => `${o.q},${o.dvt},${o.r},${o.s1}`)
        .map(o => ({
          c: o.c,
          q: o.q,
          y: o.y,
          qsort: this.lku.Question[o.q].sort,
          dvt: o.dvt,
          dvtsort: o.dvt ? this.lku.DataValueType[o.dvt].sort : 0,
          r: o.r,
          rsort: o.r ? this.lku.Response[o.r].sort : 0,
          s1: o.s1,
          s1sort: o.s1 ? this.lku.Stratification[o.s1].sort : 0,
        })).orderBy(['qsort', 'dvtsort', 'rsort', 's1sort'])
        .value();
    },
    compareColumnAlias() {
      switch (this.reportType) {
        case 'Locations':
          return 'l';
        case 'Demographics':
          return 's2';
        case 'Years':
          return 'y';
        default:
          return 'xx';
      }
    },
    tableSummary() {
      return this.$store.getters['CR/tableSummary'](this.categoryId);
    },
  },
  methods: {
    removeClicked(event) {
      // console.log('removeClicked:', event.target.dataset.id);
      event.preventDefault();
      this.$modal.show('remove-modal', { id: event.target.dataset.id, categoryId: this.categoryId });
    },
  },
  render(h) {
    let lastQuestionId;
    let lastDataValueTypeId;
    let lastResponseId;

    return h('table', {
      attrs: {
        summary: this.tableSummary,
        id: `tbl_${this.categoryId}`,
      },
    }, [
      h('thead', [
        h('tr', [
          h('th', {
            attrs: {
              scope: 'col',
            },
            style: {
              width: this.printerFriendly ? '175px' : '250px',
            },
          }, 'Indicator'),
          h('th', {
            attrs: {
              scope: 'col',
            },
            style: {
              width: this.printerFriendly ? '110px' : '200px',
            },
          }, 'Data Type'),
          h('th', {
            attrs: {
              scope: 'col',
            },
            style: {
              width: '120px',
            },
          }, 'Response'),
          h('th', {
            attrs: {
              scope: 'col',
            },
            style: {
              width: '110px',
            },
          }, 'Disability Status & Types'),
          this.compareColumns.map((col) => {
            let removeLink = [];
            if (this.compareColumns.length > 1 && !this.printerFriendly) {
              removeLink = [
                h('br'),
                h('a', {
                  attrs: {
                    'data-id': col.id,
                    class: 'normal',
                    href: '#',
                  },
                  on: {
                    click: this.removeClicked,
                  },
                }, 'Remove'),
              ];
            }
            return h('th', {
              style: {
                width: '110px',
              },
            }, _.concat([], col.name, removeLink));
          }),
        ]),
      ]),
      h(
        'tbody',
        this.rowsMinusColumnValues.map((row, index, collection) => {
          const indicatorHasData = row.dvt; // row.dvt is undefined when the indicator has no data for the selected stratifications
          const isLastRowForIndicator = collection.length <= index + 1 ? true
            : collection[index + 1].q !== row.q;
            // console.log(row, index, collection.length, isLastRowForIndicator);
          const trAttributes = {
            class: {
              pagebreakafter: isLastRowForIndicator,
            },
          };
          const indicator = h('CRTableIndicatorDataTypeCell', {
            props: {
              cellType: 'Indicator',
              rowspan: _(this.rowsMinusColumnValues).filter({ q: row.q }).value().length,
              questionId: row.q,
              categoryId: row.c,
              yearId: row.y,
            },
          });
          let dataType;
          if (indicatorHasData) {
            dataType = h('CRTableIndicatorDataTypeCell', {
              props: {
                cellType: 'DataType',
                rowspan: _(this.rowsMinusColumnValues)
                  .filter({ q: row.q, dvt: row.dvt }).value().length,
                dataValueTypeId: row.dvt,
                categoryId: row.c,
                yearId: row.y,
              },
            });
          } else {
            dataType = h('th', {
              attrs: {
                scope: 'row',
              },
            });
          }
          const response = h('th', {
            attrs: {
              rowspan: indicatorHasData ? _(this.rowsMinusColumnValues)
                .filter({ q: row.q, dvt: row.dvt, r: row.r }).value().length : 1,
              scope: indicatorHasData ? 'rowgroup' : 'row',
            },
          }, indicatorHasData ? this.lku.Response[row.r].name : '');
          const disabilityStatusAndType = h('th', {
            attrs: {
              scope: 'row',
            },
          }, indicatorHasData ? this.lku.Stratification[row.s1].name : '');
          const compareCols = this.compareColumns.map((col) => {
            if (indicatorHasData) {
              const filter = {
                q: row.q, dvt: row.dvt, r: row.r, s1: row.s1,
              };
              filter[this.compareColumnAlias] = col.id;
              const colRow = _(this.catData).filter(filter).head();
              if (colRow) {
                const { decimalPlaces } = this.dataTypes[row.dvt];
                const toFixed = val => Number(val).toFixed(decimalPlaces);
                const line1 = `${colRow.dv ? toFixed(colRow.dv) : ''}${colRow.fs ? colRow.fs : ''}`;
                let line2 = [];
                if (this.showCI) line2 = [h('br'), colRow.lci && colRow.hci ? `(${toFixed(colRow.lci)} - ${toFixed(colRow.hci)})` : ''];
                let line3 = [];
                if (this.showWN) line3 = [h('br'), colRow.wn ? numeral(colRow.wn).format('0,0') : ''];
                return h('td', _.concat([], line1, line2, line3));
              }
              return h('td');
            }
            return h('td');
          });
          if (indicatorHasData) {
            if (lastQuestionId !== row.q) {
              lastQuestionId = row.q;
              lastDataValueTypeId = row.dvt;
              lastResponseId = row.r;
              return h('tr', trAttributes, [
                indicator,
                dataType,
                response,
                disabilityStatusAndType,
                compareCols,
              ]);
            } else if (lastDataValueTypeId !== row.dvt) {
              lastDataValueTypeId = row.dvt;
              lastResponseId = row.r;
              return h('tr', trAttributes, [
                dataType,
                response,
                disabilityStatusAndType,
                compareCols,
              ]);
            } else if (lastResponseId !== row.r) {
              lastResponseId = row.r;
              return h('tr', trAttributes, [
                response,
                disabilityStatusAndType,
                compareCols,
              ]);
            }
            return h('tr', trAttributes, [
              disabilityStatusAndType,
              compareCols,
            ]);
          }
          return h('tr', trAttributes, [
            indicator,
            dataType,
            response,
            disabilityStatusAndType,
            compareCols,
          ]);
        }),
      ),
    ]);
  },
};

</script>

  <style scoped>
    /* CSS trick to keep the table from always going to 100% width
      causing the columns to ignore the widths of the thead th cells.
      From https://stackoverflow.com/a/4457905 */
    table {
      table-layout: fixed;
      width: 1px;
      /* margin-right: 15px; */
    }
    table, td, th {
        border-spacing: 0;
        border: 1px solid gray;
        border-collapse: collapse;
        line-height: 20px;
        color: black;
    }

    .normal {
      font-weight: normal;
    }

    td {
      text-align: center;
    }

    thead th {
      background-color: #C1DED5;
      text-align: center;
    }

    tbody th {
      font-weight: normal;
    }

    tr th[scope='rowgroup']:not(.indicator),
    tr th[scope='row'] {
      text-align: center;
    }

    .pagebreakafter {
      page-break-after: always;
    }
  </style>



// WEBPACK FOOTER //
// src/components/CR/CRTable.vue